import React, { useState, useEffect, useCallback } from 'react';
import { X, Shield, BarChart3, Megaphone, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Link } from 'react-router-dom';

/**
 * COOKIE CONSENT BANNER - GDPR, CCPA, PDPA COMPLIANT
 * 
 * Compliance Notes:
 * - GDPR Art. 7: Consent must be freely given, specific, informed, unambiguous
 * - CCPA: Honors "Do Not Sell or Share My Personal Information" right
 * - PDPA: Requires explicit opt-in consent before collecting personal data
 * 
 * Key Implementation Decisions:
 * - No pre-ticked boxes (GDPR requirement)
 * - No auto-accept on scroll or close (explicit choice required)
 * - Analytics and Marketing are OFF by default (GDPR/PDPA)
 * - Consent stored with timestamp and version for audit trail
 * - Users can revoke consent anytime via footer link
 * 
 * Google Analytics Integration:
 * - GA is loaded but defaults to 'denied' state (no tracking)
 * - When user accepts Analytics, gtag consent is updated to 'granted'
 * - When user rejects Analytics, gtag consent remains 'denied'
 * - Existing consent is checked on page load via index.html script
 */

// Consent storage key and version for tracking policy changes
const CONSENT_STORAGE_KEY = 'niva_cookie_consent';
const CONSENT_VERSION = '1.0.0';
const CONSENT_EXPIRY_DAYS = 365;

/**
 * Cookie categories with descriptions
 * - Necessary: Always on, required for site functionality
 * - Analytics: Opt-in, for understanding user behavior
 * - Marketing: Opt-in, for personalized advertising
 */
const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Necessary',
    description: 'Essential cookies required for the website to function. These cannot be disabled.',
    required: true,
    icon: Shield,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-100'
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors interact with our website. This data is anonymized and used to improve our services.',
    required: false,
    icon: BarChart3,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-100',
    ccpaNote: 'Under CCPA, you can opt out of the sale or sharing of your personal information.'
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    description: 'Used to deliver personalized advertisements and measure their effectiveness across platforms.',
    required: false,
    icon: Megaphone,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-100'
  }
};

/**
 * Utility function to get current consent state
 * Can be imported and used by analytics/marketing scripts
 * @returns {Object} Consent state with categories, timestamp, and version
 */
export const getCookieConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    
    const consent = JSON.parse(stored);
    
    // Check if consent has expired
    const expiryDate = new Date(consent.timestamp);
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
    
    if (new Date() > expiryDate) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    
    // Check if consent version matches current version
    if (consent.version !== CONSENT_VERSION) {
      // Version mismatch - consent needs to be renewed
      // Keeping old consent but flagging it
      return { ...consent, versionMismatch: true };
    }
    
    return consent;
  } catch (e) {
    console.error('Error reading cookie consent:', e);
    return null;
  }
};

/**
 * Check if a specific cookie category is consented
 * @param {string} category - 'necessary', 'analytics', or 'marketing'
 * @returns {boolean} Whether the category is consented
 */
export const hasConsentFor = (category) => {
  const consent = getCookieConsent();
  if (!consent) return category === 'necessary'; // Necessary is always allowed
  return consent.categories[category] === true;
};

/**
 * Save consent state to localStorage
 * @param {Object} categories - Object with category consent states
 */
const saveConsent = (categories) => {
  const consentData = {
    categories,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    // Store user agent for audit purposes (anonymized)
    userAgent: navigator.userAgent.substring(0, 50)
  };
  
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
  
  // Update Google Analytics consent state
  // This calls the function defined in index.html
  if (typeof window.updateGoogleConsent === 'function') {
    window.updateGoogleConsent(
      categories.analytics === true,
      categories.marketing === true
    );
  }
  
  // Dispatch custom event for other scripts to react to consent changes
  window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consentData }));
};

/**
 * Clear consent (for testing or user request)
 */
export const clearCookieConsent = () => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('cookieConsentCleared'));
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be changed
    analytics: false, // Off by default (GDPR/PDPA requirement)
    marketing: false  // Off by default (GDPR/PDPA requirement)
  });

  // Check for existing consent on mount
  useEffect(() => {
    const existingConsent = getCookieConsent();
    
    if (!existingConsent || existingConsent.versionMismatch) {
      // No consent or version changed - show banner
      setShowBanner(true);
      
      // If there was previous consent, pre-fill preferences
      if (existingConsent?.categories) {
        setPreferences({
          necessary: true,
          analytics: existingConsent.categories.analytics || false,
          marketing: existingConsent.categories.marketing || false
        });
      }
    }
  }, []);

  // Handle Accept All - consents to all categories
  const handleAcceptAll = useCallback(() => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    saveConsent(allConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Handle Reject All - accepts necessary only (GDPR compliant)
  const handleRejectAll = useCallback(() => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    saveConsent(minimalConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Handle Save Preferences - confirms granular choices
  const handleSavePreferences = useCallback(() => {
    saveConsent(preferences);
    setShowBanner(false);
    setShowPreferences(false);
  }, [preferences]);

  // Toggle individual preference
  const togglePreference = useCallback((category) => {
    if (category === 'necessary') return; // Cannot toggle necessary
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  // Don't render if banner shouldn't be shown
  if (!showBanner) return null;

  return (
    <>
      {/* Overlay for accessibility - prevents interaction with page until choice is made */}
      <div 
        className="fixed inset-0 bg-black/20 z-[9998]" 
        aria-hidden="true"
      />
      
      {/* Cookie Consent Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t-2 border-gray-200 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {/* Main Banner Content */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Text Content */}
            <div className="flex-1">
              <h2 
                id="cookie-consent-title" 
                className="text-lg md:text-xl font-semibold text-gray-900 mb-2"
              >
                We use cookies to improve your experience.
              </h2>
              <p 
                id="cookie-consent-description" 
                className="text-sm md:text-base text-gray-600 mb-3"
              >
                We use cookies and similar technologies to enhance your browsing experience, 
                analyze site traffic, and personalize content. You can choose which cookies 
                you allow. For more details, see our{' '}
                <Link to="/privacy-policy" className="text-red-600 hover:underline font-medium">
                  Privacy Policy
                </Link>.
              </p>
              
              {/* CCPA Notice - Required for California residents */}
              <p className="text-xs text-gray-500 italic">
                California residents: You have the right to opt out of the sale or sharing 
                of your personal information under CCPA.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 lg:ml-6 lg:flex-shrink-0">
              <Button
                variant="outline"
                onClick={handleRejectAll}
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
                data-testid="cookie-reject-all"
              >
                Reject All
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPreferences(!showPreferences)}
                className="border-red-600 text-red-600 hover:bg-red-50"
                data-testid="cookie-manage-preferences"
              >
                Manage Preferences
                {showPreferences ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="cookie-accept-all"
              >
                Accept All
              </Button>
            </div>
          </div>

          {/* Preferences Panel - Inline expansion */}
          {showPreferences && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Cookie Preferences
              </h3>
              
              <div className="space-y-4">
                {Object.values(COOKIE_CATEGORIES).map((category) => {
                  const Icon = category.icon;
                  return (
                    <div 
                      key={category.id}
                      className="flex items-start justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-10 h-10 ${category.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className={category.iconColor} size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">
                              {category.name}
                            </span>
                            {category.required && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                Always Active
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {category.description}
                          </p>
                          {category.ccpaNote && (
                            <p className="text-xs text-gray-500 mt-1 italic">
                              {category.ccpaNote}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Toggle Switch - Disabled for necessary cookies */}
                      <div className="ml-4 flex-shrink-0">
                        {category.required ? (
                          <div className="w-11 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                            <div className="w-4 h-4 bg-white rounded-full" />
                          </div>
                        ) : (
                          <Switch
                            checked={preferences[category.id]}
                            onCheckedChange={() => togglePreference(category.id)}
                            aria-label={`Toggle ${category.name} cookies`}
                            data-testid={`cookie-toggle-${category.id}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save Preferences Button */}
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * Cookie Settings Button - For footer or floating access
 * Allows users to update their consent anytime (GDPR requirement)
 */
export const CookieSettingsButton = ({ className = '' }) => {
  const handleOpenSettings = () => {
    // Clear existing consent to show banner again
    clearCookieConsent();
    // Force page refresh to show banner
    window.location.reload();
  };

  return (
    <button
      onClick={handleOpenSettings}
      className={`text-sm text-gray-400 hover:text-red-500 transition-colors ${className}`}
      aria-label="Open cookie settings"
    >
      Cookie Settings
    </button>
  );
};

export default CookieConsent;
